<template>
  <div id="mol-member-list">
    <!-- Loading Overlay -->
    <v-overlay :value="OverlayLoadingFlag">
      <v-progress-circular
        indeterminate
        size="64"
        color="#0097d7"
      ></v-progress-circular>
    </v-overlay>

    <!-- Premium Header Card -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" class="no-gutters">
            <v-col cols="12" sm="7" class="d-flex align-center gap-4">
              <div class="header-icon-box">
                <v-icon size="48" color="white">mdi-account-multiple</v-icon>
              </div>
              <div>
                <h1 class="header-title">{{ PageTitle }}</h1>
                <p class="header-subtitle">
                  Manage and track prospective members
                </p>
              </div>
            </v-col>
            <v-col
              cols="12"
              sm="5"
              class="d-flex justify-end align-center gap-2"
            >
              <v-btn class="action-btn" @click.prevent="refreshPageData">
                <v-icon left>mdi-refresh</v-icon>
                Refresh
              </v-btn>
              <router-link to="/mission-one-lakh/dashboard">
                <v-btn class="action-btn">
                  <v-icon left>mdi-arrow-left</v-icon>
                  Back
                </v-btn>
              </router-link>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Information Card -->
    <v-card elevation="12" class="info-card mb-6">
      <v-container class="pa-6">
        <v-row>
          <v-col cols="12" sm="6">
            <div class="info-item">
              <v-icon color="#0097d7" size="28">mdi-calendar-clock</v-icon>
              <div class="ml-4">
                <span class="info-label">Deadline Date</span>
                <p class="info-value">{{ MolSettingValues.DeadLineDateTxt }}</p>
              </div>
            </div>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="info-item">
              <v-icon color="#0097d7" size="28"
                >mdi-account-multiple-plus</v-icon
              >
              <div class="ml-4">
                <span class="info-label">Total Members</span>
                <p class="info-value">{{ rows.length }} prospective members</p>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Instructions Card -->
    <v-card elevation="12" class="instructions-card mb-6">
      <v-container class="pa-8">
        <h2 class="instruction-heading">
          <v-icon left color="#ff9800">mdi-lightbulb-on</v-icon>
          Key Information
        </h2>
        <div class="instruction-content">
          <div class="instruction-paragraph">
            <v-icon color="#4caf50">mdi-check-circle</v-icon>
            <span
              >Identify and add potential members who could benefit from JCI
              membership.</span
            >
          </div>
          <div class="instruction-paragraph">
            <v-icon color="#4caf50">mdi-check-circle</v-icon>
            <span
              ><strong>Action:</strong> Fill in the form below to add member
              details.</span
            >
          </div>
          <div class="instruction-paragraph">
            <v-icon color="#4caf50">mdi-check-circle</v-icon>
            <span
              ><strong>Requirement:</strong> Minimum
              {{ MolSettingValues.MinimumCount }} - Maximum
              {{ MolSettingValues.MaximumCount }} members.</span
            >
          </div>
          <div class="requirement-box mt-6">
            <v-icon color="#ff9800" size="28">mdi-alert-circle</v-icon>
            <div>
              <p class="mb-3"><strong>Important Notes:</strong></p>
              <ul>
                <li>
                  Minimum {{ MolSettingValues.MinimumCount }} prospective
                  members are required
                </li>
                <li>
                  Maximum {{ MolSettingValues.MaximumCount }} members allowed
                </li>
                <li>All information must be accurate and complete</li>
                <li>Use the form below to add new members</li>
              </ul>
            </div>
          </div>
        </div>
      </v-container>
    </v-card>

    <!-- Loading State -->
    <v-container class="py-0" v-if="StartupLoadingFlag">
      <v-row wrap>
        <v-col cols="12" md="4" v-for="n in 6" :key="n">
          <v-skeleton-loader type="card-avatar"></v-skeleton-loader>
        </v-col>
      </v-row>
    </v-container>

    <!-- Search Loading State -->
    <v-container class="py-0" v-if="SearchFlag && !StartupLoadingFlag">
      <v-skeleton-loader type="table-tbody"></v-skeleton-loader>
    </v-container>

    <!-- Create Member Card (Inline Form) -->
    <v-card
      elevation="12"
      class="create-card mb-6"
      v-if="!SearchFlag && !StartupLoadingFlag"
    >
      <v-container class="pa-6">
        <h2 class="create-title mb-6">
          <v-icon color="#0097d7" size="28">mdi-plus-circle</v-icon>
          Add New Member
        </h2>

        <!-- Age Requirement Note -->
        <v-card class="requirement-note mb-6" elevation="0">
          <div class="requirement-note-content">
            <v-icon color="#ff9800" size="22" class="mr-3"
              >mdi-alert-circle</v-icon
            >
            <div>
              <strong>Important Requirement:</strong> The member should be
              within <strong>18 to 40 years</strong> of age
            </div>
          </div>
        </v-card>

        <v-form ref="createForm" v-model="createFormValid" lazy-validation>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <div class="form-field">
                <label class="field-label required-field">Full Name</label>
                <v-text-field
                  :rules="[(v) => !!v || 'Full Name is required']"
                  v-model="newMember.MemberName"
                  type="text"
                  dense
                  outlined
                  prepend-icon="mdi-account-circle"
                  placeholder="Enter full name"
                  class="field-input"
                ></v-text-field>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <div class="form-field">
                <label class="field-label required-field"
                  >WhatsApp Number</label
                >
                <v-text-field
                  :counter="10"
                  v-mask="'##########'"
                  :rules="[
                    (v) => !!v || 'WhatsApp Number is required',
                    (v) => (v && v.length === 10) || 'Must be 10 digits',
                  ]"
                  v-model="newMember.PhoneNumber"
                  type="tel"
                  dense
                  outlined
                  prepend-icon="mdi-phone"
                  placeholder="Enter 10 digit number"
                  class="field-input"
                ></v-text-field>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <div class="form-field">
                <label class="field-label required-field">Email Address</label>
                <v-text-field
                  :rules="[
                    (v) => !!v || 'Email is required',
                    (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
                  ]"
                  v-model="newMember.EmailID"
                  type="email"
                  dense
                  outlined
                  prepend-icon="mdi-email"
                  placeholder="Enter email address"
                  class="field-input"
                ></v-text-field>
              </div>
            </v-col>

            <v-col cols="12" class="d-flex justify-end gap-3 align-center">
              <v-btn
                @click="resetCreateForm"
                outlined
                class="action-cancel-btn"
              >
                <v-icon left>mdi-close</v-icon>
                Clear
              </v-btn>
              <span class="btn-separator"></span>

              <v-btn
                :disabled="!createFormValid"
                @click="submitNewMember"
                :loading="SubmitFlag"
                class="action-add-member-btn"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Add Member
              </v-btn>
              <span class="btn-separator">OR</span>
              <v-btn
                class="action-bulk-upload-btn"
                @click.prevent="openBulkUploadDialog"
              >
                <v-icon left>mdi-cloud-upload</v-icon>
                Bulk Upload
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-card>

    <!-- Data Table Card -->
    <v-card
      elevation="12"
      class="table-card mb-6"
      v-if="!SearchFlag && rows.length > 0"
    >
      <v-container class="pa-6">
        <h2 class="table-title mb-6">
          <v-icon color="#0097d7" size="28">mdi-account-multiple</v-icon>
          {{ rows.length }} Prospective Members Found
        </h2>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Search members..."
              single-line
              hide-details
              outlined
              dense
              class="search-field"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-data-table
              class="mol-members-table"
              v-model="selected"
              :search="search"
              :items="rows"
              :headers="TableColumns1"
              :item-key="TableOptions1.ItemKey"
              :single-select="TableOptions1.SingleSelectFlag"
              :items-per-page="TableOptions1.ItemsPerPage"
              :footer-props="TableOptions1.FooterProps"
            >
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template
                    v-slot:activator="{ on, attrs }"
                    v-if="item.EditFlag"
                  >
                    <v-icon
                      icon
                      small
                      color="warning"
                      @click="editAlert(item)"
                      v-bind="attrs"
                      v-on="on"
                      class="action-icon-btn"
                    >
                      mdi-file-document-edit
                    </v-icon>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
                &nbsp;&nbsp;&nbsp;
                <v-tooltip bottom>
                  <template
                    v-slot:activator="{ on, attrs }"
                  >
                    <v-icon
                      icon
                      small
                      color="red"
                      @click="deleteData(item)"
                      v-bind="attrs"
                      v-on="on"
                      class="action-icon-btn"
                    >
                      mdi-delete
                    </v-icon>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Empty State Card -->
    <v-card
      elevation="12"
      class="empty-state-card"
      v-if="!SearchFlag && !StartupLoadingFlag && rows.length === 0"
    >
      <v-container class="pa-12 text-center">
        <v-icon size="100" color="#bdbdbd" class="mb-6"
          >mdi-inbox-outline</v-icon
        >
        <h2 class="mb-4" style="color: #757575">No members yet</h2>
        <p class="mb-8" style="color: #999">
          Start adding prospective members. Click the "Add Member" button above
          to get started!
        </p>
        <v-btn
          v-if="TableOptions1.CreateFlag"
          @click.prevent="addRecordPrompt = true"
          color="#0097d7"
          large
          rounded
        >
          <v-icon left>mdi-plus-circle</v-icon>
          Add First Member
        </v-btn>
      </v-container>
    </v-card>

    <v-dialog v-model="EditRecordPrompt" persistent max-width="70%">
      <mol-member-edit
        pageTitle="Edit Mission OneLakh Member"
        :EditRecordPrompt="EditRecordPrompt"
        @hideEditRecordPrompt="hideEditRecordPrompt"
        :recordData="EditRowData"
        v-if="EditRecordPrompt"
      ></mol-member-edit>
    </v-dialog>

    <v-dialog v-model="BulkUploadPrompt" persistent max-width="60%">
      <mol-member-bulk-upload
        pageTitle="Bulk Upload Mission OneLakh Members"
        :BulkUploadPrompt="BulkUploadPrompt"
        @hideBulkUploadPrompt="hideBulkUploadPrompt"
        v-if="BulkUploadPrompt"
      ></mol-member-bulk-upload>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import MolMemberEdit from "@/view/pages/erp/lom/mission-onelakh/mol-member/MolMemberEdit.vue";
import MolMemberBulkUpload from "@/view/pages/erp/lom/mission-onelakh/mol-member/MolMemberBulkUpload.vue";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {
    MolMemberEdit,
    MolMemberBulkUpload,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      TableSelectFlag: false,
      CreateFlag: true,
      OverlayLoadingFlag: false,
      LomCode: "",
      rows: [],
      TableColumns1: [],
      TableOptions1: {
        CreateFlag: false,
      },
      selected: [],
      search: "",

      EditRecordPrompt: false,
      EditRowData: "",
      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      MolSettingValues: [],
      BulkUploadPrompt: false,

      // New member form data
      createFormValid: true,
      newMember: {
        MemberName: "",
        PhoneNumber: "",
        EmailID: "",
      },
    };
  },
  mounted() {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        var ZoneCode = this.$session.get("ZoneId");
        ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
        console.log("ZoneCode=" + ZoneCode);
        this.ZoneCode = ZoneCode;

        this.pageData();
      }
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "mol_non_member",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
      this.getMolSetting();
      this.getPhase2MinCount();
    },
    pageData() {
      console.log("pageData called");
      this.StartupLoadingFlag = true;
      this.getTableRecords();
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideEditRecordPrompt(flag) {
      console.log("hideEditRecordPrompt called");
      this.EditRecordPrompt = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    hideBulkUploadPrompt(flag) {
      console.log("hideBulkUploadPrompt called");
      this.BulkUploadPrompt = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    openBulkUploadDialog() {
      console.log("openBulkUploadDialog called");
      this.BulkUploadPrompt = true;
    },

    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    resetCreateForm() {
      console.log("resetCreateForm called");
      this.newMember = {
        MemberName: "",
        PhoneNumber: "",
        EmailID: "",
      };
      this.$refs.createForm.reset();
    },
    submitNewMember() {
      console.log("submitNewMember called");
      const validate = this.$refs.createForm.validate();
      console.log("validate=" + validate);

      if (validate) {
        Swal.fire({
          title: "Are you sure?",
          text: "You want to add this member!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.isConfirmed) {
            this.addNewMember();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        this.toast("error", "Kindly fill the required fields");
      }
    },
    addNewMember() {
      console.log("addNewMember is called");

      const validate = this.$refs.createForm.validate();
      console.log("validate=" + validate);

      if (validate) {
        this.SubmitFlag = true;

        var upload = {
          UserInterface: 1,
          MemberName: this.newMember.MemberName,
          MobileNumber: this.newMember.PhoneNumber,
          EmailID: this.newMember.EmailID,
          DateOfBirth: "",
          MemberAddress: "",
          MemberPhoto: "",
          City: "",
        };
        console.log({ upload });

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/mission-onelakh/mol-members-add";

        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.resetCreateForm();
              thisIns.getTableRecords();
              thisIns.sweetAlert("success", output, true);
              thisIns.toast("success", output);
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.toast("error", "Error adding member");
          });
      } else {
        var message = "Kindly fill the required fields";
        this.sweetAlert("error", message, false);
      }
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var YearCode = this.JciYearCode;
      console.log("YearCode=" + YearCode);

      this.rows = [];

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/mission-onelakh/mol-members-list";
      var upload = {
        UserInterface: 1,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      this.rows = [];
      this.SearchFlag = true;
      this.LoadingFlag = true;
      this.OverlayLoadingFlag = true;

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
          thisIns.StartupLoadingFlag = false;
          thisIns.OverlayLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          records = response.data.records;
          console.log(
            "output=" + output + ", flag=" + flag + ", records=" + records
          );

          var options = records.TableOptions;
          thisIns.TableOptions1 = options;

          if (flag == 1) {
            thisIns.TableColumns1 = records.TableHeader;
            thisIns.rows = records.TableData;
            thisIns.TableSelectFlag = records.TableSelectFlag;
            thisIns.CreateFlag = records.TableOptions.CreateFlag;
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
        });
    },
    editAlert(tr) {
      console.log("editAlert called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        // var CityId = tr.CityId;
        // this.member = tr;
        this.EditRowData = tr;
        this.EditRecordPrompt = true;
      } else {
        var message = "Kindly select one record to edit";
        this.errorMessage(message);
      }
    },
    deleteData(tr) {
      console.log("deleteData called");
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });
      if (n1 > 0) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to delete this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.deleteRecord(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
        // this.deleteRecord(tr);
      } else {
        var message = "Kindly select one member to delete";
        this.toast("error", message);
      }
    },
    deleteRecord(tr) {
      // add todo in store and clear all fields in dialog
      console.log("deleteRecord called");

      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });

      if (n1 > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/mission-onelakh/mol-member-delete";
        var upload = {
          UserInterface: 1,
          MolMember: tr.MolMemberId,
          Lom: tr.MolId,
          MemberName: tr.MemberName,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.getTableRecords();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        console.log("some errors occured");
        var message = "Kindly select one member to delete";
        this.sweetAlert("error", message, false);
      }
    },
    getMolSetting() {
      console.log("getMolSetting called");

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/mission-onelakh/mol-setting-show";
      var upload = {
        UserInterface: 2,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      this.SearchFlag = true;
      this.LoadingFlag = true;

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          records = response.data.records;
          console.log(
            "output=" + output + ", flag=" + flag + ", records=" + records
          );

          if (flag == 1) {
            thisIns.MolSettingValues = records;
            // thisIns.toast("success", output);
          } else {
            // thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.SearchFlag = false;
          thisIns.LoadingFlag = false;
        });
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
// Color Palette - JCI Brand Colors
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$text-off-black: #0a0f29;
$text-white: #ffffff;

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

#mol-member-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  .header-card {
    background: linear-gradient(
      135deg,
      $navy 0%,
      $aqua 50%,
      $navy 100%
    ) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 40px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(255, 255, 255, 0.1) 50%,
        transparent 70%
      );
      animation: shimmer 3s infinite;
    }

    .header-gradient {
      position: relative;
      z-index: 1;
    }
    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.15) 0%,
        transparent 70%
      );
      animation: rotate 25s linear infinite;
    }
    .header-content {
      position: relative;
      z-index: 2;
    }
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 16px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.5);
    }
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
    margin: 0;
    padding: 0;
  }

  .header-subtitle {
    font-size: 17px;
    opacity: 0.98;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
    margin: 6px 0 0 0;
  }

  .action-btn {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;
    font-size: 12px !important;
    padding: 8px 16px !important;

    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
      background: rgba(255, 255, 255, 0.25) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
    }

    .v-icon {
      transition: all 0.3s ease;
      margin-right: 6px;
    }
    &:hover .v-icon {
      transform: scale(1.15);
    }
  }

  .info-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #e8f4f8 0%, #f0f7ff 100%) !important;
    box-shadow: 0 8px 24px rgba(58, 103, 177, 0.12) !important;
    border: 2px solid rgba(0, 151, 215, 0.25) !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s backwards;

    .info-item {
      display: flex;
      align-items: center;
      padding: 16px 14px;
      background: rgba(255, 255, 255, 0.8);
      border-radius: 12px;
      transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
      border: 1px solid rgba(0, 151, 215, 0.1);

      &:hover {
        background: rgba(255, 255, 255, 1);
        box-shadow: 0 8px 20px rgba(0, 151, 215, 0.2);
        transform: translateY(-4px);
      }

      .v-icon {
        transition: all 0.3s ease;
        min-width: 28px;
      }
      &:hover .v-icon {
        transform: scale(1.15) rotate(5deg);
      }

      .ml-4 {
        flex: 1;
      }
      .info-label {
        font-size: 11px;
        font-weight: 800;
        color: #888;
        text-transform: uppercase;
        letter-spacing: 0.6px;
        display: block;
        margin-bottom: 6px;
      }
      .info-value {
        font-size: 16px;
        font-weight: 800;
        margin: 0;
        color: $navy;
        letter-spacing: 0.2px;
      }
    }
  }

  .instructions-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #fff5e6 0%, #fffaf0 100%) !important;
    box-shadow: 0 12px 32px rgba(237, 190, 56, 0.15) !important;
    border: 2px solid rgba(237, 190, 56, 0.25) !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s backwards;

    .instruction-heading {
      font-size: 16px;
      font-weight: 800;
      color: $navy;
      display: flex;
      align-items: center;
      margin: 0 0 20px 0;
      text-transform: uppercase;
      letter-spacing: 0.6px;

      .v-icon {
        transition: all 0.3s ease;
        margin-right: 10px;
      }
      &:hover .v-icon {
        transform: rotate(20deg) scale(1.15);
      }
    }

    .instruction-content {
      .instruction-paragraph {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        margin: 14px 0;
        padding: 12px 14px;
        font-size: 14px;
        line-height: 1.7;
        color: $text-off-black;
        background: rgba(255, 255, 255, 0.6);
        border-radius: 10px;
        border-left: 4px solid #4caf50;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.9);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          transform: translateX(4px);
        }

        .v-icon {
          flex-shrink: 0;
          margin-top: 2px;
          transition: all 0.3s ease;
        }
        &:hover .v-icon {
          transform: scale(1.2);
        }
      }

      .requirement-box {
        display: flex;
        gap: 16px;
        background: linear-gradient(
          135deg,
          rgba(255, 152, 0, 0.12),
          rgba(255, 152, 0, 0.08)
        );
        border-left: 5px solid #ff9800;
        padding: 18px 16px;
        border-radius: 12px;
        margin-top: 20px;
        transition: all 0.3s ease;

        &:hover {
          background: linear-gradient(
            135deg,
            rgba(255, 152, 0, 0.15),
            rgba(255, 152, 0, 0.1)
          );
          box-shadow: 0 6px 16px rgba(255, 152, 0, 0.15);
          transform: translateY(-2px);
        }

        .v-icon {
          flex-shrink: 0;
          transition: all 0.3s ease;
        }
        &:hover .v-icon {
          transform: scale(1.2) rotate(-10deg);
        }

        p {
          margin: 0;
          font-weight: 800;
          color: $navy;
          font-size: 14px;
          letter-spacing: 0.3px;
        }
        ul {
          margin: 12px 0 0 0;
          padding-left: 24px;
          color: $text-off-black;
          li {
            margin: 8px 0;
            font-size: 13px;
            font-weight: 600;
            line-height: 1.6;
          }
        }
      }
    }
  }

  .table-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  .table-title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
  }

  .search-field {
    transition: all 0.3s ease;
    border-radius: 8px;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }
  }

  .mol-members-table {
    &::v-deep {
      thead {
        tr {
          th {
            background: linear-gradient(
              135deg,
              #f5f7fa 0%,
              #eef2f7 100%
            ) !important;
            font-weight: 800 !important;
            color: $navy !important;
            border: none !important;
          }
        }
      }

      tbody {
        tr {
          transition: all 0.3s ease;
          border-bottom: 1px solid #e8eef5 !important;

          &:hover {
            background: rgba(0, 151, 215, 0.05) !important;
            box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.08) !important;
          }

          td {
            color: $text-off-black;
            font-size: 14px;
            padding: 14px 16px !important;
          }
        }
      }
    }
  }

  .action-icon-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    cursor: pointer;

    &:hover {
      transform: scale(1.3) !important;
    }
  }

  .empty-state-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: none !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;
  }

  .gap-2 {
    gap: 8px;
  }

  .create-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 12px 32px rgba(58, 103, 177, 0.12) !important;
    border: 2px solid rgba(0, 151, 215, 0.1) !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s backwards;
  }

  .create-title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
  }

  .form-field {
    margin-bottom: 0;

    .field-label {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: $navy;
      display: block;
      margin-bottom: 6px;

      &.required-field::after {
        content: "*";
        color: #f44336;
        margin-left: 4px;
      }
    }

    .field-input {
      transition: all 0.3s ease !important;

      &::v-deep {
        .v-input__prepend-outer {
          margin-right: 8px !important;
          .v-icon {
            color: $aqua !important;
          }
        }

        .v-input__control {
          .v-input__slot {
            input,
            textarea {
              color: $text-off-black;
              font-weight: 600;

              &::placeholder {
                color: #aaa !important;
                font-weight: 500;
              }
            }
          }
        }

        fieldset {
          border-color: rgba(0, 151, 215, 0.2) !important;
        }

        &.v-input--is-focused {
          fieldset {
            border-color: $aqua !important;
          }
          .v-icon {
            color: $navy;
          }
        }
      }
    }
  }

  .requirement-note {
    border-radius: 12px !important;
    background: linear-gradient(135deg, #fff8e1 0%, #fffde7 100%) !important;
    border-left: 5px solid #ff9800 !important;
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.15) !important;

    .requirement-note-content {
      display: flex;
      align-items: center;
      padding: 16px 16px;
      color: $text-off-black;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.6;

      .v-icon {
        flex-shrink: 0;
      }
    }
  }

  .action-cancel-btn {
    font-weight: 700 !important;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #666 !important;
    border-color: #ddd !important;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

    &:hover {
      border-color: $aqua !important;
      color: $aqua !important;
      transform: translateY(-4px);
      box-shadow: 0 8px 20px rgba(0, 151, 215, 0.2) !important;
    }

    .v-icon {
      transition: all 0.3s ease;
    }
    &:hover .v-icon {
      transform: scale(1.15);
    }
  }

  .action-add-member-btn {
    background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: white !important;
    border: none !important;
    box-shadow: 0 12px 32px rgba(76, 175, 80, 0.3) !important;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

    &:hover:not(:disabled) {
      transform: translateY(-6px);
      box-shadow: 0 16px 48px rgba(76, 175, 80, 0.4) !important;
    }

    &:disabled {
      background: linear-gradient(135deg, #ccc 0%, #aaa 100%) !important;
      box-shadow: none !important;
    }

    .v-icon {
      transition: all 0.3s ease;
      color: white !important;
    }
    &:hover:not(:disabled) .v-icon {
      transform: scale(1.2);
    }
  }

  .action-bulk-upload-btn {
    background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%) !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: white !important;
    border: none !important;
    box-shadow: 0 12px 32px rgba(255, 152, 0, 0.3) !important;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 16px 48px rgba(255, 152, 0, 0.4) !important;
    }

    .v-icon {
      transition: all 0.3s ease;
      color: white !important;
    }
    &:hover .v-icon {
      transform: scale(1.2);
    }
  }

  .btn-separator {
    font-weight: 700 !important;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #999 !important;
    font-size: 12px !important;
    padding: 0 12px;
    align-self: center;
  }

  .action-save-btn {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 12px 32px rgba(0, 151, 215, 0.3) !important;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

    &:hover:not(:disabled) {
      transform: translateY(-6px);
      box-shadow: 0 16px 48px rgba(0, 151, 215, 0.4) !important;
    }

    &:disabled {
      background: linear-gradient(135deg, #ccc 0%, #aaa 100%) !important;
      box-shadow: none !important;
    }

    .v-icon {
      transition: all 0.3s ease;
    }
    &:hover:not(:disabled) .v-icon {
      transform: scale(1.2);
    }
  }

  @media (max-width: 960px) {
    padding: 20px 12px;

    .header-title {
      font-size: 28px;
    }
    .header-subtitle {
      font-size: 14px;
    }
    .header-icon-box {
      padding: 12px;
    }
    .table-title {
      font-size: 16px;
    }
    .action-btn {
      padding: 6px 12px !important;
      font-size: 10px !important;
    }

    .mol-members-table::v-deep tbody tr td {
      padding: 10px 12px !important;
      font-size: 13px;
    }
  }

  @media (max-width: 600px) {
    padding: 16px 10px;

    .header-card {
      margin-bottom: 16px;
      border-radius: 16px;
      .header-content {
        padding: 12px !important;
      }
    }

    .header-title {
      font-size: 22px;
    }
    .header-subtitle {
      font-size: 13px;
    }
    .action-btn {
      padding: 6px 10px !important;
      font-size: 9px !important;
    }

    .info-card,
    .instructions-card,
    .table-card,
    .create-card {
      margin-bottom: 16px;
      border-radius: 14px;
    }

    .mol-members-table {
      &::v-deep {
        tbody tr td {
          padding: 8px 10px !important;
          font-size: 12px;
        }
      }
    }

    .empty-state-card {
      padding: 20px 16px !important;
      .v-icon {
        font-size: 60px !important;
      }
    }
  }
}
</style>